/**
 * P7-5 — the office Re-send dialog's pure layer (plan § 20).
 *
 * Everything here is a pure function over the P7-3 preview so the money properties can be tested
 * without rendering: which lines may be selected, which need a deliberate review tick first, what the
 * selection costs, and the token that makes the whole confirm retry-safe.
 *
 * ── WHY THE OFFICE NEEDS THIS SURFACE AT ALL ───────────────────────────────────────────────────────
 * A fully-shipped order is invisible to the ENTIRE production board (§ 14.1: `resetLineOpen` flips the
 * line to OPEN, and `isShippedUnproducedLine` is `FULFILLED && BATCHED`, so not even the
 * `shippedUnproduced` lane sees it). There is no list to lean on — the office reaches these orders by
 * order number, which is why the entry point lives on the order card.
 */

import type {
  ResendConfirmResponse,
  ResendLineOutcome,
  ResendPreviewLine,
  ResendPreviewResponse,
} from '@fsp/contracts';

/**
 * ⛔ THE § 19.6 REQUIREMENT, and it is a MONEY gate rather than copy.
 *
 * A line already carrying outstanding rows in ANOTHER cohort must not be re-sendable on the operator's
 * default action. Two different tokens raced concurrently is the one double-spend P7-4 deliberately does
 * NOT refuse server-side — because refusing a second cohort would refuse real work (PR-3b lets two
 * production runs of one line flush side by side, and two lost parcels genuinely owe both). So the
 * server reports and the DIALOG withholds.
 *
 * ⚠️ MIRRORS `crossRunReview` in `reprint-queue-page.tsx`, deliberately, because it is the SAME hazard
 * one surface earlier — a line with garments in flight from more than one run. Do not simplify it to a
 * boolean "I saw the warning": consent is keyed on the SIBLING COUNT, so if another re-send or a bench
 * report lands while the dialog is open, the tick EXPIRES and the operator has to look again. A `Set` of
 * lineIds was the shape that made the queue's first version of this gate unsafe one round trip later.
 */
export function needsReview(line: ResendPreviewLine, otherCohorts: number): boolean {
  return otherCohorts > 0 && line.status === 'eligible';
}

/**
 * The consent token: what the operator is agreeing to, not that they agreed.
 *
 * Includes `resendQty` as well as the sibling count so a refund landing mid-dialog also expires the
 * tick — the server would refuse that confirm anyway (the fingerprint moves), but expiring here means
 * the operator sees WHY instead of a bare 400.
 */
export function reviewTokenOf(line: ResendPreviewLine, otherCohorts: number): string {
  return `${otherCohorts}|${line.resendQty ?? '?'}`;
}

/**
 * How many outstanding rows another cohort holds for this line.
 *
 * ⚠️ Read from the CONFIRM response's per-line outcome when we have one, and otherwise UNKNOWN — the
 * preview's `outstanding.inQueue` is a boolean for the whole ORDER and is NOT per-line attributable
 * (`QueuedReprintRef` aggregates with no garment→line mapping, which its own schema doc explains). So
 * before the first confirm we can only say "something is in the queue for this order", never a count.
 *
 * Which is why the pre-confirm gate below uses `inQueue` — the conservative direction — rather than a
 * number we cannot derive.
 */
export function preConfirmOtherCohorts(line: ResendPreviewLine): number {
  // 1 means "at least one, count unknown". Deliberately not 0: an unknown must dominate toward the
  // gate firing, never toward it staying quiet (`[[unknown-must-dominate-not-contribute-empty]]`).
  return line.outstanding.inQueue || line.outstanding.reissueSheetIds.length > 0 ? 1 : 0;
}

/** Lines the API will accept at all. `needs_override` is refused unconditionally until the attestation
 *  ships (§ 19.3), so it is shown and explained but never selectable — offering a tick the server
 *  refuses is how an operator learns the dialog lies. */
export function isSelectable(line: ResendPreviewLine): boolean {
  return line.status === 'eligible';
}

export type SelectionState = {
  /** Eligible lines the operator has EXCLUDED. Default-on, like the queue's own list. */
  deselected: ReadonlySet<string>;
  /** lineId → the review token the operator ticked. A stale token is NOT consent. */
  reviewed: ReadonlyMap<string, string>;
};

export const EMPTY_SELECTION: SelectionState = { deselected: new Set(), reviewed: new Map() };

/**
 * Is this line going to be sent?
 *
 * A review-needing line is selected ONLY while its ticked token still matches — that is the whole
 * expiry mechanism. Everything else is default-on minus explicit exclusions.
 */
export function isSelected(
  line: ResendPreviewLine,
  otherCohorts: number,
  sel: SelectionState,
): boolean {
  if (!isSelectable(line)) return false;
  if (needsReview(line, otherCohorts)) {
    return sel.reviewed.get(line.lineId) === reviewTokenOf(line, otherCohorts);
  }
  return !sel.deselected.has(line.lineId);
}

/** The lineIds a confirm would send, in preview order. */
export function selectedLineIds(
  preview: ResendPreviewResponse,
  sel: SelectionState,
  otherCohortsFor: (l: ResendPreviewLine) => number = preConfirmOtherCohorts,
): string[] {
  return preview.lines.filter((l) => isSelected(l, otherCohortsFor(l), sel)).map((l) => l.lineId);
}

/**
 * What the CURRENT selection would spend. Recomputed from the selection, never from
 * `preview.totals` — those totals cover every ELIGIBLE line, which is not what the operator ticked.
 * Rendering `preview.totals` beside a partial selection is how a confirm dialog names the wrong number.
 */
export function selectionCost(
  preview: ResendPreviewResponse,
  sel: SelectionState,
  otherCohortsFor: (l: ResendPreviewLine) => number = preConfirmOtherCohorts,
): { lines: number; garments: number; films: number } {
  let lines = 0;
  let garments = 0;
  let films = 0;
  for (const l of preview.lines) {
    if (!isSelected(l, otherCohortsFor(l), sel)) continue;
    const qty = l.resendQty ?? 0;
    lines += 1;
    // Each owed copy is a whole shirt, so this is also the replacement-BLANK count.
    garments += qty;
    films += qty * l.garments.length;
  }
  return { lines, garments, films };
}

/**
 * The arithmetic behind the count, as a sentence (§ 17.1 — the operator gets the derivation, not just
 * its answer). `null` when the basis cannot explain it, so the caller renders nothing rather than a
 * confident-looking wrong sum.
 */
export function basisSentence(line: ResendPreviewLine): string | null {
  const { currentQty, unfulfilledQty } = line.basis;
  if (currentQty === undefined || line.resendQty === null) return null;
  return `${currentQty} ordered net of refunds − ${unfulfilledQty} never shipped = ${line.resendQty} owed`;
}

/**
 * ONE token per confirm-dialog SESSION — the P7-4 contract (§ 19.1).
 *
 * ⚠️ MINTED ONCE WHEN THE DIALOG OPENS AND HELD FOR ITS WHOLE LIFE. That is not a detail, it IS the
 * atomicity mechanism: each garment is a separate `UpdateItem` server-side, so a confirm that fails
 * part-way through must be retryable, and only an identical token makes the written rows merge as
 * no-ops instead of minting a second set. Re-minting per ATTEMPT double-spends on a retry; a constant
 * merges two independent re-sends into one film.
 *
 * ⚠️ Charset matches `ResendConfirmBodySchema`'s regex (`[A-Za-z0-9._-]{8,64}`). `crypto.randomUUID`
 * satisfies it; the fallback exists only for a non-secure context and is still 32 hex chars.
 */
export function mintResendToken(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The headline a confirm's result deserves.
 *
 * ⚠️ LEADS WITH `queuedGarments` — the NEWLY created rows — never with a 2xx. P7-4 answers 200 for a
 * completely refused confirm, and a line whose garments all merged reports `already_queued` with
 * `queuedGarments: 0`. Reading a 200 as success is exactly the mistake the contract warns about, and it
 * is what would let an operator walk away believing 4 shirts were queued when the spend was zero.
 */
export function resultSummary(r: ResendConfirmResponse): {
  tone: 'ok' | 'warn' | 'none';
  headline: string;
} {
  const shirt = (n: number) => `${n} shirt${n === 1 ? '' : 's'}`;
  if (r.queuedGarments > 0) {
    const extra: string[] = [];
    if (r.alreadyQueuedLines > 0) extra.push(`${r.alreadyQueuedLines} line(s) were already queued`);
    if (r.refusedLines > 0) extra.push(`${r.refusedLines} refused`);
    return {
      tone: 'ok',
      headline: `Queued ${shirt(r.queuedGarments)} to reprint${extra.length ? ` · ${extra.join(' · ')}` : ''}.`,
    };
  }
  if (r.mergedGarments > 0) {
    return {
      tone: 'warn',
      headline: `Nothing new was queued — ${shirt(r.mergedGarments)} for this order ${r.mergedGarments === 1 ? 'was' : 'were'} already in the reprint queue under this re-send.`,
    };
  }
  return {
    tone: 'none',
    headline: 'Nothing was queued. Every line was refused — see the reasons below.',
  };
}

/** Per-line result copy. Server refusals are rendered VERBATIM (§ 16.4 — nothing machine-reads them). */
export function outcomeLabel(o: ResendLineOutcome): { tone: 'ok' | 'warn' | 'bad'; text: string } {
  if (o.status === 'queued') {
    return {
      tone: 'ok',
      text: `Queued ${o.queuedGarments} shirt${o.queuedGarments === 1 ? '' : 's'}`,
    };
  }
  if (o.status === 'already_queued') {
    return { tone: 'warn', text: 'Already queued under this re-send — nothing new was spent' };
  }
  return { tone: 'bad', text: o.refusal ?? 'Refused.' };
}
